import React, { useEffect, useState } from 'react';
import { BellRing, X, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import push from '../services/push';
import { toast } from 'sonner';

/**
 * One-time in-app prompt asking the user to opt into web-push notifications.
 * Design goals:
 *   - Never appears if push isn't supported, permission is already granted,
 *     or the user is already subscribed on this device.
 *   - Appears at most once per user on this browser — dismissal is stored
 *     under `swiftdash_push_optin_dismissed_<user_id>` so signing in again
 *     with a different account gets its own chance.
 *   - A gentle 4-second delay so it doesn't punch the user in the face the
 *     instant they land on the home page after login.
 */
const dismissKey = (uid) => `swiftdash_push_optin_dismissed_${uid || '__guest__'}`;

const PushOptInBanner = () => {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    let timer;

    const decide = async () => {
      if (!user) return;
      if (!push.supported()) return;
      if (push.permission() === 'granted') {
        // Already granted — check whether a subscription exists on this device.
        // If it does we're done; if not, we still don't nag (user has already
        // said "yes" once — the profile page can re-subscribe silently).
        return;
      }
      if (push.permission() === 'denied') return;
      const dismissed = localStorage.getItem(dismissKey(user.id));
      if (dismissed) return;
      timer = setTimeout(() => { if (alive) setVisible(true); }, 4000);
    };
    decide();
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [user]);

  const dismiss = () => {
    setVisible(false);
    if (user) localStorage.setItem(dismissKey(user.id), String(Date.now()));
  };

  const enable = async () => {
    setBusy(true);
    try {
      await push.enable();
      toast.success("You'll get order updates on this device.");
      dismiss();
    } catch (e) {
      // If the user hits 'Block' in the browser prompt, remember that too so
      // we don't keep asking on every page load.
      if (String(e.message).toLowerCase().includes('denied')) {
        dismiss();
      }
      toast.error(e.message || 'Could not turn on notifications.');
    } finally { setBusy(false); }
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Enable notifications"
      data-testid="push-optin-banner"
      className="fixed z-50 bottom-4 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-md bg-white border border-emerald-200 shadow-xl rounded-2xl p-4 flex items-start gap-3 animate-in fade-in slide-in-from-bottom-2"
    >
      <div className="h-10 w-10 shrink-0 rounded-xl bg-emerald-600 text-white flex items-center justify-center">
        <BellRing className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-900">Get order updates instantly</div>
        <p className="text-sm text-slate-600 mt-0.5">
          Enable notifications and we&apos;ll ping you when the store confirms your order, when it&apos;s ready, and when the driver&apos;s on the way.
        </p>
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={enable}
            disabled={busy}
            data-testid="push-optin-enable"
            className="h-9 px-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Turn on
          </button>
          <button
            onClick={dismiss}
            data-testid="push-optin-dismiss"
            className="h-9 px-3 rounded-full text-slate-500 hover:text-slate-800 text-sm font-medium"
          >
            Not now
          </button>
        </div>
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="push-optin-close"
        className="text-slate-300 hover:text-slate-500"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default PushOptInBanner;
